/**
 * SIGN-IN. The POC's login screen (`core/auth/login/`, `main` @ b9c1b73), on
 * this repo's stack and against PharmOS API 1.0.0.
 *
 * THE SHAPE IS THE POC's: a full screen split in two — what the platform IS on
 * the left, the step you are on to the right of it, a footer under both. The
 * left half names the company once the right half has answered which one, and
 * nothing before that.
 *
 * THREE STEPS, in the POC's order:
 *   1. credentials   company id · staff id · password   → POST /v1/user/login
 *   2. newPassword   only when the server says the credential is spent
 *                    → POST /v1/user/login/reset-password
 *   3. context       which shop you are standing in
 *                    → POST /v1/user/login/branches/{branchCode}
 *
 * THE ONE-TIME PASSWORD'S STEP SITS BETWEEN THE OTHER TWO, not after them: the
 * number has just been typed and nothing about a branch has been asked yet.
 *
 * WHAT IS NOT DRAWN, AND WHY: the system-status strip, the shift-handover banner
 * and the counter declaration. Each needs a fact PharmOS API 1.0.0 does not
 * state, and a sign-in screen is the wrong place to invent one. They are listed
 * in `docs/TODO.md`.
 *
 * REFUSALS ARE WORDED FROM THE STATUS ALONE — error codes are deferred
 * (`docs/DECISIONS.md`, 18-09-2026), so this cannot yet say which of the three
 * fields was wrong.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useErrorText } from "@/components/feedback/error-text";
import { clearToken, writeToken } from "@/api/token";
import {
  ContextPanel,
  CredentialsPanel,
  LoginFooter,
  NewPasswordPanel,
  PlatformPanel,
} from "@/features/auth";
import {
  MAX_ATTEMPTS,
  PASSWORD_MIN_LENGTH,
  PIN_LENGTH,
  credentialPolicyOk,
} from "@/domain/credential-policy";
import { applyAccountLanguage, useT } from "@/i18n";
import { chooseBranch, login, resetExpiredPassword, type LoginPayload } from "@/lib/auth/api";
import { buildSession, clearSession, saveSession, sessionKeys, sessionQuery } from "@/lib/auth/session";

/** Only a path on this site may be a redirect target: never another origin. */
export function safeRedirect(value: string | undefined): string {
  return value && value.startsWith("/") && !value.startsWith("//") ? value : "/";
}

export const Route = createFileRoute("/login")({
  validateSearch: z.object({ redirect: z.string().optional() }),
  beforeLoad: async ({ context, search }) => {
    const session = await context.queryClient.query(sessionQuery);
    if (session) throw redirect({ href: safeRedirect(search.redirect) });
  },
  component: LoginPage,
});

type Step = "credentials" | "newPassword" | "context";

function LoginPage() {
  const { t, locale } = useT();
  const errorText = useErrorText();
  const { redirect: target } = Route.useSearch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("credentials");
  const [companyId, setCompanyId] = useState("");
  const [staffId, setStaffId] = useState("");
  const [password, setPassword] = useState("");
  const [payload, setPayload] = useState<LoginPayload | null>(null);
  const [storeId, setStoreId] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [policyError, setPolicyError] = useState<string | null>(null);

  const branches = payload?.branches ?? [];
  const zh = locale === "zh-HK";
  const locked = attempts >= MAX_ATTEMPTS;

  /** Takes a payload from step 1 or step 2 and moves to whatever it calls for. */
  function accept(next: LoginPayload) {
    writeToken(next.authToken, "interim");
    applyAccountLanguage(next.displayLang);
    setPayload(next);
    // A company with one branch: preselected, because a list of one is a
    // statement rather than a choice. The press still belongs to the operator.
    const only = next.branches?.length === 1 ? next.branches[0]?.branchCode : undefined;
    setStoreId(only ?? null);
    setStep(next.isPasswordExpired === true ? "newPassword" : "context");
  }

  const signIn = useMutation({
    mutationFn: () => {
      clearSession(); // a fresh sign-in never carries the last one's token
      return login({ tenantId: companyId.trim(), username: staffId.trim(), password });
    },
    onSuccess: accept,
    // A failed attempt, counted once. Both the credential and the branch write
    // count against the same three, so the number the operator is told never
    // depends on which door refused.
    onError: () => setAttempts((n) => n + 1),
  });

  const replacePassword = useMutation({
    mutationFn: (next: string) => resetExpiredPassword(next),
    onSuccess: (next, submitted) => {
      // The new password becomes the one this sign-in uses.
      setPassword(submitted);
      accept(next);
    },
  });

  const enterBranch = useMutation({
    mutationFn: async (branchCode: string) => {
      const token = await chooseBranch(branchCode);
      writeToken(token, "active");
      if (!payload) throw new Error("no login payload");
      return buildSession(payload, branchCode, companyId.trim());
    },
    onSuccess: async (session) => {
      saveSession(session);
      queryClient.setQueryData(sessionKeys.current(), session);
      await navigate({ href: safeRedirect(target) });
    },
    onError: () => clearToken(), // the interim token is all that is left
  });

  function startOver() {
    clearSession();
    setPayload(null);
    setStoreId(null);
    setPassword("");
    setPolicyError(null);
    setStep("credentials");
  }

  function submitCredentials() {
    // NAMES BOTH SHAPES. A message about password length in front of somebody
    // holding six digits sends them to look for a credential never issued.
    if (!credentialPolicyOk(password)) {
      setPolicyError(t("auth.credentialShape", { min: PASSWORD_MIN_LENGTH, pin: PIN_LENGTH }));
      return;
    }
    setPolicyError(null);
    signIn.mutate();
  }

  const company = payload?.company;
  const companyName = company?.companyNameEn?.trim() ?? companyId.trim();
  const companyNameZh = company?.companyNameZh?.trim() ?? null;

  return (
    <div className="flex min-h-dvh flex-col bg-bg">
      <main className="grid min-h-0 flex-1 bg-surface lg:grid-cols-2">
        <PlatformPanel
          {...(payload
            ? {
                companyName: zh && companyNameZh ? companyNameZh : companyName,
                companyNameAlt: zh ? companyName : companyNameZh,
                companyId: companyId.trim(),
                branchCount: branches.length,
              }
            : {})}
        />

        {step === "credentials" && (
          <CredentialsPanel
            companyId={companyId}
            staffId={staffId}
            password={password}
            onCompanyId={setCompanyId}
            onStaffId={setStaffId}
            onPassword={(value) => {
              setPolicyError(null);
              setPassword(value);
            }}
            onSubmit={submitCredentials}
            signingIn={signIn.isPending}
            error={
              policyError ??
              (signIn.error
                ? locked
                  ? t("auth.accountLockedRefusal")
                  : `${errorText(signIn.error)} ${t("auth.attemptsRemaining", {
                      n: MAX_ATTEMPTS - attempts,
                    })}`
                : null)
            }
            attempts={attempts}
            locked={locked}
          />
        )}

        {step === "newPassword" && payload && (
          <NewPasswordPanel
            staffId={payload.staffId}
            busy={replacePassword.isPending}
            error={replacePassword.error ? errorText(replacePassword.error) : null}
            onSubmit={(next) => replacePassword.mutate(next)}
            onBack={startOver}
          />
        )}

        {step === "context" && payload && (
          <ContextPanel
            staffName={
              (zh ? payload.staffNameZh?.trim() : payload.staffNameEn?.trim()) ??
              payload.staffNameEn?.trim() ??
              payload.staffId
            }
            staffId={payload.staffId}
            companyName={zh && companyNameZh ? companyNameZh : companyName}
            companyNameAlt={zh ? companyName : companyNameZh}
            companyId={companyId.trim()}
            branches={branches}
            storeId={storeId}
            onStoreId={setStoreId}
            zh={zh}
            signingIn={enterBranch.isPending}
            error={enterBranch.error ? errorText(enterBranch.error) : null}
            onBack={startOver}
            onSubmit={() => storeId && enterBranch.mutate(storeId)}
          />
        )}
      </main>
      <LoginFooter />
    </div>
  );
}
